import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Plus, Target } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge, leadStatusVariant } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select, Textarea } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { formatRelative } from '../lib/utils'

type Lead = {
  id: string
  companyName: string
  contactName: string | null
  email: string | null
  status: string
  industry: string | null
  sourceChannel: string | null
  estimatedValueAmount: string | null
  nextActionAt: string | null
  updatedAt: string
  metadata?: { jobPitch?: { pdfObjectKey?: string } } | null
}

type ListResult = { results: Lead[]; total: number }

export function LeadsPage() {
  const qc = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['leads', { status: statusFilter }],
    queryFn: () =>
      apiClient.get<ListResult>(`/leads${statusFilter ? `?status=${statusFilter}` : ''}`),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post('/leads', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] })
      setDialogOpen(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const body = Object.fromEntries(fd.entries())
    create.mutate(body)
  }

  return (
    <AppLayout>
      <PageHeader
        title="Leads"
        description={`${data?.total ?? 0} lead in pipeline`}
        action={
          <div className="flex gap-2">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tutti</option>
              <option value="new">Nuovi</option>
              <option value="contacted">Contattati</option>
              <option value="qualified">Qualificati</option>
              <option value="proposal">Proposta</option>
              <option value="negotiation">Trattativa</option>
              <option value="won">Vinti</option>
              <option value="lost">Persi</option>
            </Select>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="size-4" /> Nuovo lead
            </Button>
          </div>
        }
      />

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Azienda</th>
              <th className="text-left px-5 py-3 font-medium">Contatto</th>
              <th className="text-left px-5 py-3 font-medium">Fonte</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
              <th className="text-left px-5 py-3 font-medium">Prossima azione</th>
              <th className="text-left px-5 py-3 font-medium">Pitch</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                <tr key={i} className="border-t border-zinc-800">
                  <td className="px-5 py-3" colSpan={6}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : data && data.results.length > 0 ? (
              data.results.map((lead) => (
                <tr
                  key={lead.id}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40 transition"
                >
                  <td className="px-5 py-3">
                    <div className="font-medium text-zinc-100">{lead.companyName}</div>
                    {lead.industry && <div className="text-xs text-zinc-500">{lead.industry}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-zinc-300">{lead.contactName ?? '—'}</div>
                    {lead.email && <div className="text-xs text-zinc-500">{lead.email}</div>}
                  </td>
                  <td className="px-5 py-3 text-zinc-400 text-xs">{lead.sourceChannel ?? '—'}</td>
                  <td className="px-5 py-3">
                    <Badge variant={leadStatusVariant(lead.status)}>{lead.status}</Badge>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-500">
                    {lead.nextActionAt ? formatRelative(lead.nextActionAt) : '—'}
                  </td>
                  <td className="px-5 py-3">
                    {lead.metadata?.jobPitch?.pdfObjectKey ? (
                      <a
                        href={`/api/jobpitch/${lead.id}/pdf`}
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                      >
                        <Download className="size-3.5" /> PDF
                      </a>
                    ) : (
                      <span className="text-xs text-zinc-600">—</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>
                  <Empty
                    icon={Target}
                    title="Nessun lead"
                    description="Aggiungi un primo prospect commerciale per iniziare a tracciare il funnel."
                    action={
                      <Button onClick={() => setDialogOpen(true)}>
                        <Plus className="size-4" /> Nuovo lead
                      </Button>
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nuovo lead"
        description="Solo nome azienda obbligatorio."
        size="lg"
      >
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div>
            <Label htmlFor="companyName">Azienda *</Label>
            <Input id="companyName" name="companyName" required placeholder="ACME Srl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="contactName">Referente</Label>
              <Input id="contactName" name="contactName" placeholder="Mario Rossi" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="phone">Telefono</Label>
              <Input id="phone" name="phone" />
            </div>
            <div>
              <Label htmlFor="industry">Settore</Label>
              <Input id="industry" name="industry" />
            </div>
            <div>
              <Label htmlFor="sourceChannel">Fonte</Label>
              <Select id="sourceChannel" name="sourceChannel" defaultValue="">
                <option value="">—</option>
                <option value="cold_email">Cold email</option>
                <option value="referral">Referral</option>
                <option value="web">Web</option>
                <option value="linkedin">LinkedIn</option>
                <option value="event">Evento</option>
                <option value="other">Altro</option>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="notes">Note</Label>
            <Textarea id="notes" name="notes" />
          </div>
          {create.isError && (
            <div className="text-sm text-red-400">
              {(create.error as Error)?.message ?? 'Errore'}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creazione…' : 'Crea lead'}
            </Button>
          </div>
        </form>
      </Dialog>
    </AppLayout>
  )
}
